import type { IUser } from '~/entities/user/user/interfaces/user'

import type { IContent } from '~/features-shared/content/interfaces/content'
import type { ID, SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

/**
 * Wiki page visibilty (public or private).
 * @info FromPrivateParent = 0: private page (which has a private parent (not directly private))
 * @info Public = 1: public page
 * @info Private = 2: private page (which has a public parent or is the wiki root page)
 */
export enum WikiVisibility {
  FromPrivateParent = 0,
  Public = 1,
  Private = 2,
}

/**
 * Wiki content (Wiki page).
 * Wiki page for a specific edition index (by default is the latest edition).
 * @extends IContent
 */
export interface IWiki extends IContent {
  /** User who do this current edition. */
  last_publisher: IUser

  /** User's uuid who do this current edition. */
  last_publisher_hashkey: string

  /** Is it a minor edition (not a revision). */
  is_minor_edit: boolean

  /** Nb editions (minus the current edition). */
  nb_editions: number

  /** Nb of revisions (major editions) (minus the current edition if it is a revision). */
  nb_revisions: number

  /**
   * Revision (major edition) description.
   * @info Empty string if minor edition.
   */
  revision_description: string

  /**
   * Revision (major edition) index.
   * @info [ 0 to nb_revisions - 1 ] OR -1.
   * Equal to -1 when:
   * - It is a minor edition.
   * - It is the current edition (revision or minor).
   */
  revision_index: number

  /**
   * Wiki page visibilty (public or private).
   * @info FromPrivateParent = 0: private page (which has a private parent (not directly private))
   * @info Public = 1: public page
   * @info Private = 2: private page (which has a public parent or is the wiki root page)
   */
  visibility: WikiVisibility

  /**
   * Is the wiki page edition is locked (not able to edit it).
   * @info 0: edition enabled.
   * @info 1: edition disabled.
   */
  locked: 0 | 1

  /** Is wiki root page. */
  diff: boolean

  /** Is wiki root page. */
  is_root: boolean

  /** Nb users having editided this wiki page. */
  nb_contributors: number

  /** Edition tag to know when there is an edit conflict for this wiki page. */
  content_etag: string
}

/**
 * Wiki tree item (Wiki page info in the Wiki tree).
 */
export interface WikiTreeItemResponseResult {
  /** Wiki page subject URI. */
  subject6wuri: SUBJECT_6W_URI

  /** Wiki page id. */
  id: ID

  /** Wiki page title. */
  title: string

  /** Wiki page depth in the tree (root page is 0). */
  depth: number

  /**
   * Wiki page visibilty (public or private).
   * @info FromPrivateParent = 0: private page (which has a private parent (not directly private))
   * @info Public = 1: public page
   * @info Private = 2: private page (which has a public parent or is the wiki root page)
   */
  visibility: WikiVisibility
  /** True, if wiki page is locked */
  locked: boolean
}

/**
 * Wiki Edition Response Result returned by the wiki service backend API.
 */
export interface WikiEditionResponseResult {
  /**
   * Edition's index
   * @info [ 0 to nb_editions - 1 ].
   */
  edition_index: number

  /** User who do the edition. */
  publisher: IUser

  /**
   * Edition date.
   * @example '2023-06-13T12:56:47.000Z'
   */
  creation_date: string

  /**
   * Edition description (Revision description).
   * @info Empty string if minor edition or the edition is a revert from Revision 0.
   */
  description: string

  /**
   * Revision (major edition) index.
   * @info [ 0 to nb_revisions - 1 ] OR -1 if the edition is a minor.
   */
  revision_index: number
}

/**
 * Wiki Edition Diff Response Result returned by the wiki service backend API.
 */
export interface WikiEditionDiffResponseResult {
  /** Wiki title html diff between the two editions. */
  title_html_diff: string

  /** Wiki content html diff between the two editions. */
  body_html_diff: string
}

/**
 * Wiki edition content (edition content contains the wiki title and the wiki content).
 */
export interface WikiEditionContent {
  /** Wiki edition title. */
  title: string

  /** Wiki edition content. */
  content: string
}

/**
 * Wiki History Edition used by the views to display the editions timeline.
 */
export interface WikiHistoryEdition {
  /** User who do the edition. */
  author: IUser

  /**
   * Edition description (Revision description).
   * @info Empty string if minor edition or the edition is a revert from Revision 0.
   */
  description: string

  /** Edition date. */
  date: Date

  /** Is it a revision (a major edition). */
  isRevision: boolean

  /**
   * Edition's index
   * @info [ 0 to nb_editions - 1 ].
   */
  index: number

  /**
   * Revision index (major edition index).
   * @info [ 0 to nb_revisions - 1 ] OR NaN if the edition is a minor.
   */
  revisionIndex: number

  /**
   * Wiki edition content (wiki edition content for this specific edition).
   * @info undefined means that the wiki edition content has not been fetched yet from the backend.
   */
  content?: WikiEditionContent

  /**
   * Edition diffs content map.
   * @info key: is the edition index on which diff has been done with this edition.
   * @info value: Edition content diff to display.
   */
  diffs: Map<number, WikiEditionContent>
}

/**
 * Available filters modes for the wiki history timeline.
 * @info Full: Display full history (minor editions and revisions (major editions))
 * @info RevisionsOnly: Display only revisions (only major editions)
 */
export enum WikiHistoryFilterMode {
  Full = 'full',
  RevisionsOnly = 'revisionsOnly',
}

/**
 * Available compare with modes for the wiki history.
 * @info PreviousEdition: Compare with the previous edition
 *      (depending on the selected filter mode, may be with the previous revision)
 * @info CurrentEdition: Compare the current edition with an edition of the user's choice.
 * @info SelectedEdition: Compare two editions of the user's choice.
 */
export enum WikiHistoryCompareWithMode {
  PreviousEdition = 'previousEdition',
  CurrentEdition = 'currentEdition',
  SelectionEdition = 'selectionEdition',
}
